import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import Guid from '../components/Guid';
import Pager from '../components/Pager';
import StatusPill from '../components/StatusPill';
import LookupSelect from '../components/LookupSelect';
import ListTools from '../components/ListTools';
import ActiveFilters from '../components/ActiveFilters';
import EmptyRow from '../components/EmptyRow';
import { TABLE_SCREENS } from './tableScreens';
import { weekdaysReverse } from '../../enums/weekdays';

/**
 * One page for the five list screens the prototype draws identically:
 * Providers, Provider Admin, Facilities, Areas, Facility Redirects.
 *
 * The prototype models them as a single `tableScreen` with per-screen config, and
 * they really are the same page — a filter card, a typed table, a pager. What
 * differs is the endpoint, and each config owns its own `fetch` because these
 * screens page in several different ways (see `tableScreens.js`).
 *
 * Transactions, Payment Methods and Subscriptions are the same shape and joined
 * later, which is why the config grew a `status` cell kind and select filters
 * rather than three near-identical page files.
 *
 * The rows are read-only here. Every one of these screens has an edit form and,
 * in some cases, create and delete; those are separate screens in the old admin
 * and separate sections in the prototype, so they are ports of their own rather
 * than something to smuggle in. Each row links to the old detail page until
 * that port happens.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const money = value => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : `${n.toFixed(2)} DKK`;
};

// The response shape differs per endpoint: an array, `{ areas, pages }`, or
// `{ providerAdmins, pages }`. Normalising here keeps it out of the config.
function normalise(data, rowsKey) {
  if (Array.isArray(data)) return { rows: data, pages: null };
  if (!data || typeof data !== 'object') return { rows: [], pages: null };

  if (rowsKey && Array.isArray(data[rowsKey])) {
    return { rows: data[rowsKey], pages: typeof data.pages === 'number' ? data.pages : null };
  }

  const firstArray = Object.values(data).find(Array.isArray);
  return {
    rows: firstArray || [],
    pages: typeof data.pages === 'number' ? data.pages : null
  };
}

function Cell({ row, column, detail }) {
  const [key, , kind] = column;
  const value = row[key];

  if (kind === 'id') {
    // A `/next/…` target is a route inside this tree and must not reload the
    // page; anything else is still the old admin.
    const target = detail ? detail(value, row) : undefined;
    return target && target.startsWith('/next/')
      ? <Guid value={value} to={target} />
      : <Guid value={value} href={target} />;
  }

  if (kind === 'bool') {
    if (value === undefined || value === null) return <span className="next-muted">—</span>;
    const yes = value === true || value === 1 || value === '1';
    return (
      <span className={`next-pill next-pill--${yes ? 'ok' : 'neutral'}`}>
        {yes ? 'Yes' : 'No'}
      </span>
    );
  }

  if (kind === 'date') return <span className="next-nowrap">{fmtDate(value)}</span>;

  // A weekday arrives as its number, and a time as hh:mm:ss.
  if (kind === 'weekday') {
    return <span className="next-nowrap">{weekdaysReverse[value] || <span className="next-muted">—</span>}</span>;
  }

  if (kind === 'time') {
    return (
      <span className="next-nowrap">
        {value ? String(value).split(':').slice(0, 2).join(':') : <span className="next-muted">—</span>}
      </span>
    );
  }

  // Whether there is anything in the field, not what it says — for long text a
  // column cannot show.
  if (kind === 'filled') {
    return (
      <span className={`next-pill next-pill--${value ? 'ok' : 'neutral'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }

  if (kind === 'money') return <span className="next-nowrap">{money(value)}</span>;

  // Colour comes from the shared status map, so a status means the same thing on
  // every screen that shows it.
  if (kind === 'status') {
    return value ? <StatusPill status={value}>{value}</StatusPill> : <span className="next-muted">—</span>;
  }

  if (kind === 'badge') {
    return value ? <span className="next-pill next-pill--neutral">{value}</span> : <span className="next-muted">—</span>;
  }

  if (value === undefined || value === null || value === '') {
    return <span className="next-muted">—</span>;
  }

  return <span className={kind === 'strong' ? 'next-stack__main' : undefined}>{String(value)}</span>;
}

Cell.propTypes = {
  row: PropTypes.object.isRequired,
  column: PropTypes.array.isRequired,
  detail: PropTypes.func
};

const TableScreenInternal = ({ api, screenKey, categories, systemTypes }) => {
  const screen = TABLE_SCREENS[screenKey];
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [pages, setPages] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query);

  const page = parseInt(query.page, 10) || 1;
  const perPage = parseInt(query.resultsPerPage, 10) || 15;

  const orderBy = query.orderBy || '';
  const orderDirection = query.orderDirection === 'asc' ? 'asc' : 'desc';

  useEffect(() => {
    setDraft(query);
  }, [query]);

  const load = useCallback(async () => {
    setRows(null);
    setFailed(false);
    try {
      const data = await screen.fetch(api, { filters: query, page, perPage });
      const { rows: list, pages: count } = normalise(data, screen.rowsKey);
      setRows(list);
      setPages(count);
    } catch (error) {
      setFailed(true);
    }
  }, [api, screen, query, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (next, { resetPage = true } = {}) => {
    // The sort survives a search. It is not a filter — the filter row does not
    // hold it and `next` is built from that row — so applying one used to drop
    // the column somebody had just sorted by, silently, back to the default.
    const merged = {
      orderBy: query.orderBy,
      orderDirection: query.orderDirection,
      ...next
    };
    if (resetPage) delete merged.page;

    const search = Object.entries(merged)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    navigate(search ? `?${search}` : '', { replace: false });
  };

  const set = (name, value) => setDraft(previous => ({ ...previous, [name]: value }));

  const sortBy = key => apply({
    ...query,
    orderBy: key,
    orderDirection: orderBy.toLowerCase() === key.toLowerCase() && orderDirection === 'desc' ? 'asc' : 'desc'
  });

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>{screen.title}</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : [
                screen.paged ? `Page ${page}${pages ? ` of ${pages.toLocaleString()}` : ''}` : null,
                `${rows.length} row${rows.length === 1 ? '' : 's'}`,
                screen.subtitle
              ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="next-page__actions">
          {screen.create && (
            <Link className="next-btn next-btn--primary" to={screen.create.to}>
              {screen.create.label}
            </Link>
          )}
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => {
            event.preventDefault();
            apply(draft);
          }}
        >
          <div className="next-filters__row">
            {screen.filters.map(({ name, label, placeholder, type, options: fixed, optionsFrom, lookup }) => {
              // `optionsFrom` reads a list the store already holds, so a config
              // never restates one that can change.
              // Guarded: some enum modules export an object rather than the
              // array of `{ value, label }` a select needs, and a bare `.map` on
              // one takes the whole page down with it — which it did, once.
              const fromStore = {
                categories,
                systemTypes
              }[optionsFrom];

              const options = fromStore
                ? fromStore.map(({ Name }) => ({ value: Name, label: Name }))
                : (Array.isArray(fixed) ? fixed : null);

              return (
              // A filter that takes an entity id searches by name, the same way
              // the old admin's async selects did. `lookup` names the api module
              // and the config stays declarative.
              lookup ? (
                <LookupSelect
                  key={name}
                  label={label}
                  value={draft[name]}
                  onChange={value => set(name, value)}
                  loadOptions={key => lookup(api, key)}
                  placeholder={placeholder}
                />
              ) : (
              <label className="next-filter" key={name}>
                <span className="next-filter__label">{label}</span>
                {options ? (
                  <select
                    className="next-input"
                    value={draft[name] ?? ''}
                    onChange={event => set(name, event.target.value)}
                  >
                    <option value="">Any</option>
                    {options.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="next-input"
                    type={type || 'text'}
                    value={draft[name] ?? ''}
                    placeholder={placeholder}
                    onChange={event => set(name, event.target.value)}
                  />
                )}
              </label>
                )
              );
            })}
            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              <button
                type="button"
                className="next-btn next-btn--quiet"
                onClick={() => { setDraft({}); apply({}); }}
              >
                Clear
              </button>
            </div>
          </div>
        </form>
      </section>

      <ActiveFilters
        fields={screen.filters}
        query={query}
        onChange={next => { setDraft(next); apply(next); }}
      />

      <ListTools
        screenPath={`/next/${screen.path}`}
        search={location.search}
        columns={screen.columns}
        rows={rows}
        exportName={screen.path}
        perPage={perPage}
        // Only where the endpoint actually pages; on the rest, one request is
        // already the whole result and a second button would promise more.
        fetchPage={screen.paged
          ? (next => screen.fetch(api, { filters: query, page: next, perPage })
            .then(data => normalise(data, screen.rowsKey).rows))
          : undefined}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load {screen.title.toLowerCase()}.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  {screen.columns.map(([key, label, , align, sortKey]) => (
                    <th
                      key={key}
                      className={align === 'center' ? 'next-table__center' : align === 'right' ? 'next-table__right' : undefined}
                    >
                      {sortKey ? (
                        <button type="button" className="next-th-sort" onClick={() => sortBy(sortKey)}>
                          {label}
                          {orderBy.toLowerCase() === sortKey.toLowerCase()
                            ? (orderDirection === 'desc' ? ' ↓' : ' ↑')
                            : ''}
                        </button>
                      ) : label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr>
                    <td colSpan={screen.columns.length} className="next-muted next-table__note">Loading…</td>
                  </tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <EmptyRow
                    columns={screen.columns.length}
                    // "No provider admin here" rather than "no Provider Admin
                    // here": the title is a heading, this is a sentence.
                    noun={screen.noun || screen.title.replace(/s$/, '').toLowerCase()}
                    filtered={screen.filters.some(({ name }) => (
                      name !== 'page' && name !== 'resultsPerPage'
                      && query[name] !== undefined && query[name] !== ''
                    ))}
                    onClear={() => { setDraft({}); apply({}); }}
                  />
                )}
                {(rows ?? []).map((row, index) => (
                  <tr key={row.Id || row.id || row.AreaId || row.providerAdminId || index}>
                    {screen.columns.map((column, columnIndex) => (
                      <td
                        key={column[0]}
                        // Stacked on a phone, where each cell prints its own
                        // label; the first column is the row's subject and
                        // carries none. See `.next-table--stack`.
                        data-label={columnIndex === 0 ? '' : column[1]}
                        className={column[3] === 'center' ? 'next-table__center' : column[3] === 'right' ? 'next-table__right' : undefined}
                      >
                        <Cell row={row} column={column} detail={screen.detail} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {screen.paged && (
            <Pager
              page={page}
              pages={pages}
              perPage={perPage}
              hasNext={pages ? page < pages : (rows ?? []).length >= perPage}
              // Back to page 1: page 4 of a 15-row list is not page 4 of a
              // 100-row one, and staying put would land somewhere nobody asked
              // for — or past the end.
              onPerPageChange={size => apply({ ...query, resultsPerPage: size })}
              onChange={next => apply({ ...query, page: next }, { resetPage: false })}
            />
          )}
        </section>
      )}
    </div>
  );
};

TableScreenInternal.propTypes = {
  api: PropTypes.object.isRequired,
  screenKey: PropTypes.string.isRequired,
  categories: PropTypes.array,
  systemTypes: PropTypes.array
};

const TableScreen = withApi(
  connect(
    ({ categories, systemTypes }) => ({
      categories: categories || [],
      systemTypes: systemTypes || []
    }),
    {}
  )(TableScreenInternal)
);

TableScreen.requiresLogin = true;

export default TableScreen;
